import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/badge';
import { Spinner } from '@shared/components/ui/Spinner';
import { Incident, IncidentHistory as HistoryItem } from '../../models/Incident';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faBolt } from '@fortawesome/pro-light-svg-icons';
import { useIncident } from '../../hooks/useIncidents';

export interface HistoryModalRef {
  openWithIncident: (incident: Incident) => void;
  close: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface HistoryModalProps {}

export const HistoryModal = forwardRef<HistoryModalRef, HistoryModalProps>((_, ref) => {
  const { t } = useTranslation('incidentsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [currentIncident, setCurrentIncident] = useState<Incident | null>(null);
  const [shouldOpen, setShouldOpen] = useState(false);

  // Obtener el detalle de la incidencia que incluye historial
  // La llamada se ejecuta cuando se abre la modal (cuando currentIncident?.id está disponible)
  const { data: incidentDetail, isLoading } = useIncident(currentIncident?.id ?? null);

  // Extraer historial del detalle
  const history: HistoryItem[] = incidentDetail?.historial || [];

  // Abrir la modal cuando currentIncident esté establecido y modalRef esté disponible
  useEffect(() => {
    if (shouldOpen && currentIncident) {
      let attempts = 0;
      const maxAttempts = 10;

      const tryOpen = () => {
        attempts++;
        if (modalRef.current) {
          modalRef.current.openModal();
          setShouldOpen(false);
        } else if (attempts < maxAttempts) {
          requestAnimationFrame(tryOpen);
        } else {
          setShouldOpen(false);
        }
      };

      requestAnimationFrame(tryOpen);
    }
  }, [shouldOpen, currentIncident]);

  useImperativeHandle(ref, () => ({
    openWithIncident: (incident: Incident) => {
      // Establecer el incidente primero
      setCurrentIncident(incident);

      // Si el ref ya está disponible, abrir inmediatamente
      if (modalRef.current) {
        modalRef.current.openModal();
      } else {
        // Si no está disponible, marcar que debe abrirse cuando el ref esté listo
        setShouldOpen(true);
      }
    },
    close: () => {
      modalRef.current?.closeModal();
      setCurrentIncident(null);
      setShouldOpen(false);
    },
  }));

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    const timeStr = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });

    if (isToday) {
      return `Hoy ${timeStr}`;
    }

    return (
      date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }) + ` ${timeStr}`
    );
  };

  const getExecutionStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ok':
      case 'success':
        return 'bg-bg-success text-success';
      case 'error':
      case 'failed':
        return 'bg-bg-error text-error';
      case 'pending':
        return 'bg-bg-warning text-warning';
      default:
        return 'bg-card-border text-text';
    }
  };

  if (!currentIncident) return null;

  return (
    <ModalCenter ref={modalRef} title={t('detail.history.title')} width="lg" type="default">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-complementary-text text-center py-6 rounded border-card-border border bg-card-bg my-3">
            {t('detail.history.empty')}
          </p>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto py-3">
            {history.map((item, index) => {
              const isLastItem = index === history.length - 1;

              return (
                <div key={item.id} className="flex items-start gap-3 pb-2 relative">
                  {/* Línea vertical que conecta los puntos - llega hasta el siguiente punto */}
                  {/* Solo renderizar la línea si NO es el último item */}
                  {!isLastItem && (
                    <div
                      className="absolute left-1 w-px bg-lines"
                      style={{
                        // La línea empieza desde el centro del punto actual
                        // top: mt-1 (0.25rem) + mitad del punto (0.25rem) = 0.5rem
                        top: '0.5rem',
                        // altura: desde el centro del punto actual hasta el centro del siguiente
                        // = altura del item completo + space-y-4 (1rem) - mt-1 del siguiente (0.25rem) - mitad del punto siguiente (0.25rem)
                        height: 'calc(100% + 1rem - 0.5rem)',
                      }}
                    />
                  )}

                  <div className="flex-shrink-0 mt-1 relative z-10">
                    <FontAwesomeIcon icon={faCircle} className="w-2 h-2 text-complementary-text block bg-body-bg" />
                  </div>
                  <div className={`flex-1 min-w-0 space-y-2 pb-4 ${!isLastItem ? 'border-b border-card-border' : ''}`}>
                    {/* Header: Fecha */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-complementary-text">{formatDateTime(item.created_at)}</span>
                    </div>

                    {/* Nombre de la acción con badges */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faBolt} className="w-3 h-3 text-complementary-text flex-shrink-0" />
                        <p className="text-base font-medium text-text">{item.action_snapshot_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${getExecutionStatusColor(item.execution_status)}`}
                        >
                          {item.execution_status}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-card-border text-text">
                          {item.origin}
                        </Badge>
                      </div>
                    </div>

                    {/* Mensaje del courier si existe */}
                    {item.courier_response?.mensaje && (
                      <div className="text-sm text-complementary-text bg-card-bg border border-card-border rounded-lg py-2 px-4">
                        {item.courier_response.mensaje}
                      </div>
                    )}

                    {/* Payload enviado si existe */}
                    {item.payload_sent && (
                      <div>
                        {Object.entries(item.payload_sent).map(([key, value]) => {
                          if (value === null || value === undefined || value === '') {
                            return null;
                          }

                          const formatKey = (k: string) =>
                            k
                              .split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ');

                          const displayValue =
                            typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);

                          return (
                            <Badge
                              key={key}
                              variant="outline"
                              className="inline-flex items-center gap-1 nowrap mr-1 mb-1"
                            >
                              <span className="text-[10px] font-medium text-complementary-text">{formatKey(key)}:</span>
                              <span>{displayValue}</span>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Referencia externa si existe */}
                    {item.external_operation_ref && (
                      <div className="text-xs">
                        <span className="text-[10px] font-medium text-complementary-text">
                          {t('detail.history.external_ref', 'Ref. externa')}:{' '}
                        </span>
                        <span className="text-text">{item.external_operation_ref}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

HistoryModal.displayName = 'HistoryModal';
